"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const PlayerComponent_1 = __importDefault(require("../../components/player/PlayerComponent"));
const nouislider_1 = __importDefault(require("nouislider"));
const PlayerStatus_1 = require("../../types/PlayerStatus");
const shuffleArray_1 = require("../../utils/shuffleArray");
const generateIndexes_1 = require("../../utils/tracks/generateIndexes");
const getTimeString_1 = require("../../utils/getTimeString");
const showToast_1 = require("../../utils/dom/showToast");
class PlayerPresenter {
    constructor(parentElement, playerModel, tracksModel, createLikeCallback) {
        this.parentElement = parentElement;
        this.playerModel = playerModel;
        this.tracksModel = tracksModel;
        this.createLikeCallback = createLikeCallback;
        this.status = PlayerStatus_1.PlayerStatus.Stopped;
        this.volume = 1;
        this.shuffle = false;
        this.repeat = false;
        this.trackSlider = null;
        this.tracksChanged = 0;
        // Left here so there would be no TypeScript initialization error
        this.playerComponent = new PlayerComponent_1.default(this.playerModel.track, this.status, this.shuffle, this.repeat, this.playerModel.isLoading);
        this.audioContext = new AudioContext();
        this.audioElement = new Audio();
        this.load();
        this.render();
    }
    render() {
        this.playerComponent.removeElement();
        this.playerComponent = new PlayerComponent_1.default(this.playerModel.track, this.status, this.shuffle, this.repeat, this.playerModel.isLoading);
        this.parentElement.append(this.playerComponent.getElement());
        this.addHandlers();
        this.createTrackSlider(this.playerModel.track.duration);
        this.createVolumeSlider();
    }
    addHandlers() {
        this.playerComponent.addOnPlayListener(() => {
            this.play();
        });
        this.playerComponent.addOnStopListener(() => {
            this.pause();
        });
        this.playerComponent.addOnNextListener(() => {
            this.loadNextTrack();
        });
        this.playerComponent.addOnPreviousListener(() => {
            this.loadPreviousTrack();
        });
        this.playerComponent.addOnShuffleListener(() => {
            this.shuffle = !this.shuffle;
            this.onTrackListChange();
        });
        this.playerComponent.addOnRepeatListener(() => {
            this.repeat = !this.repeat;
            this.render();
        });
        this.playerComponent.addOnLikeListener(this.createLikeCallback(this.playerModel.track));
    }
    onTrackListChange() {
        this.playerModel.tracksType = this.tracksModel.tracksType;
        this.playerModel.playlistId = this.tracksModel.playlistId;
        if (this.shuffle) {
            const playerNextTracks = (0, shuffleArray_1.shuffleArray)(this.playerModel.originalTracks.filter((track) => track.id !== this.playerModel.track.id));
            this.playerModel.track.index = 0;
            playerNextTracks.unshift(this.playerModel.track);
            this.playerModel.tracks = (0, generateIndexes_1.generateIndexes)(playerNextTracks);
        }
        else {
            this.playerModel.tracks = (0, generateIndexes_1.generateIndexes)(this.playerModel.originalTracks);
        }
        this.render();
    }
    load() {
        var _a;
        this.audioElement.pause();
        this.audioElement.remove();
        this.audioElement = this.loadElement();
        this.audioElement.volume = this.volume;
        (_a = this.trackSlider) === null || _a === void 0 ? void 0 : _a.set(0);
        this.audioElement.addEventListener('loadeddata', () => {
            this.audioElement.addEventListener('timeupdate', () => {
                var _a;
                const currentTime = this.audioElement.currentTime;
                (_a = this.trackSlider) === null || _a === void 0 ? void 0 : _a.set(currentTime);
                const timeElement = document.getElementById('player-time');
                if (!timeElement) {
                    throw new Error('Element with id: player-time is not found');
                }
                timeElement.innerText = (0, getTimeString_1.getTimeString)(currentTime * 1000);
            });
            this.audioElement.addEventListener('ended', () => {
                if (this.repeat) {
                    this.audioElement.play();
                }
                else {
                    this.loadNextTrack();
                }
            });
        });
    }
    loadElement() {
        const audioElement = new Audio(`${window.process.env['SONGS_STORAGE_URL']}${this.playerModel.track.path}`);
        audioElement.crossOrigin = 'anonymous';
        const audioSource = this.audioContext.createMediaElementSource(audioElement);
        audioSource.connect(this.audioContext.destination);
        return audioElement;
    }
    async play() {
        this.playerModel.isLoading = true;
        let isTimeout = false;
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }
        try {
            const loadTimeoutId = setTimeout(() => {
                (0, showToast_1.showToast)(showToast_1.ToastType.Danger, '', 'Не удалось загрузить трек.');
                this.playerModel.isLoading = false;
                isTimeout = true;
                this.audioElement.pause();
                this.audioElement.remove();
                this.audioContext.close();
                this.audioContext = new AudioContext();
                this.render();
            }, 10000);
            this.render();
            await this.audioElement.play();
            clearTimeout(loadTimeoutId);
            if (isTimeout) {
                this.audioElement.pause();
                return;
            }
            this.status = PlayerStatus_1.PlayerStatus.Playing;
            this.render();
        }
        catch (error) {
            if (!(error instanceof DOMException)) {
                throw error;
            }
        }
        finally {
            // Remove isLoading flag with delay to prevent fast clicking on tracks
            if (this.tracksChanged === 2) {
                setTimeout(() => {
                    this.playerModel.isLoading = false;
                    this.render();
                }, 3000);
            }
            else {
                this.tracksChanged++;
                setTimeout(() => {
                    this.playerModel.isLoading = false;
                    this.render();
                }, 1000);
                setTimeout(() => {
                    this.tracksChanged = 0;
                }, 12000);
            }
        }
    }
    pause() {
        this.audioElement.pause();
        this.status = PlayerStatus_1.PlayerStatus.Stopped;
        this.render();
    }
    loadNextTrack() {
        if (this.playerModel.isLoading) {
            return;
        }
        if (!this.playerModel.tracks.filter((track) => track.id === this.playerModel.track.id)[0]) {
            const firstTrack = this.playerModel.tracks[0];
            if (!firstTrack) {
                return;
            }
            this.playerModel.track = firstTrack;
        }
        else {
            const currentIndex = this.playerModel.track.index;
            const nextIndex = (currentIndex + 1) % this.playerModel.tracks.length;
            const nextTrack = this.playerModel.tracks[nextIndex];
            if (!nextTrack) {
                throw new Error('Next track is undefined');
            }
            this.playerModel.track = nextTrack;
        }
        this.playerModel.onTrackChange();
    }
    loadPreviousTrack() {
        if (this.playerModel.isLoading) {
            return;
        }
        if (!this.playerModel.tracks.filter((track) => track.id === this.playerModel.track.id)[0]) {
            const firstTrack = this.playerModel.tracks[0];
            if (!firstTrack) {
                return;
            }
            this.playerModel.track = firstTrack;
        }
        else {
            const currentIndex = this.playerModel.track.index;
            const tracksLength = this.playerModel.tracks.length;
            const previousIndex = (currentIndex - 1 + tracksLength) % tracksLength;
            const previousTrack = this.playerModel.tracks[previousIndex];
            if (!previousTrack) {
                throw new Error('Previous track is undefined');
            }
            this.playerModel.track = previousTrack;
        }
        this.playerModel.onTrackChange();
    }
    createTrackSlider(trackDuration) {
        const slider = document.getElementById('range-play');
        if (!slider) {
            throw new Error('Element with id: "range-play" is not found');
        }
        const trackSlider = nouislider_1.default.create(slider, {
            range: {
                min: 0,
                max: trackDuration / 1000,
            },
            step: 1,
            start: [this.audioElement.currentTime],
            connect: 'lower',
            behaviour: 'tap-drag',
        });
        this.trackSlider = trackSlider;
        trackSlider.on('change', () => {
            let value = Number(trackSlider.get());
            if (value < 3) {
                value = 0;
            }
            this.audioElement.currentTime = value;
        });
    }
    createVolumeSlider() {
        const sliderElement = document.getElementById('range-volume');
        if (!sliderElement) {
            throw new Error('Element with id: "range-play" is not found');
        }
        const slider = nouislider_1.default.create(sliderElement, {
            range: {
                min: 0,
                max: 100,
            },
            step: 1,
            start: [this.volume * 100],
            connect: 'lower',
            behaviour: 'tap-drag',
        });
        slider.on('update', () => {
            const value = Number(slider.get());
            const newVolume = value / 100;
            this.volume = newVolume;
            this.audioElement.volume = newVolume;
        });
    }
}
exports.default = PlayerPresenter;
